import { getImageAltTag } from "@lib/utils/attributesUtils";
import type { FC } from "react";
import { memo } from "react";
import { space } from "@ecco/ui/tokens";
import { Box } from "@ecco/ui/box";
import ProductImage from "./ProductImage";
import { ProductImagesVideo } from "./ProductImagesVideo";
import type { ProductMedia } from "@lib/product/types";
import FibblContainer from "@components/fibbl/FibblContainer";
import { FIBBL_INDEX, isFibblEnabled } from "@components/fibbl/constants";

export interface ProductImagesProps {
    media: ProductMedia[];
    onImageClick: (imageIndex: number) => void;
    imageIndex?: number;
    altTagPattern?: string;
}

export const ProductImages: FC<ProductImagesProps> = memo(
    ({ media, onImageClick, imageIndex, altTagPattern }) => {
        const currentItem = media[imageIndex];
        if (!currentItem) return null;

        const renderCurrentItem = () => {
            if (currentItem.type === "video" || currentItem.type === "interactiveVideo") {
                return (
                    <ProductImagesVideo
                        key={currentItem.url}
                        currentlySelected={true}
                        media={currentItem}
                    />
                );
            }

            if (isFibblEnabled && imageIndex === FIBBL_INDEX) {
                return (
                    <Box
                        key="fibbl-image-0"
                        aspectRatio={"4/5"}
                        top={0}
                        w="100%"
                        left={0}
                        zIndex={1}
                    >
                        <FibblContainer
                            imageUrl={currentItem.url}
                            onImageClick={() => onImageClick(FIBBL_INDEX)}
                        />
                    </Box>
                );
            }

            return (
                <ProductImage
                    key={`product-image-${currentItem.label}`}
                    media={currentItem}
                    onImageClick={onImageClick}
                    index={imageIndex}
                    currentlySelected={true}
                    alt={getImageAltTag(altTagPattern, currentItem.label)}
                    priority={imageIndex === 0}
                />
            );
        };

        return (
            <Box
                gap={space[0.2]}
                display={["none", "none", "none", "block"]}
                position={"relative"}
                w="100%"
            >
                {renderCurrentItem()}
            </Box>
        );
    }
);

ProductImages.displayName = "ProductImages";

export default ProductImages;
